import React, { useEffect, useRef, useState } from "react";
import { Music, Play, Pause, SkipForward, SkipBack, Upload, Trash2, X, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { musicFileUrl } from "@/lib/mediaUrls";
import { toast } from "sonner";

export default function MusicPlayer() {
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [autostartAttempted, setAutostartAttempted] = useState(false);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/music");
      setTracks(data);
    } catch (e) {
      console.error("error in MusicPlayer.jsx", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-start playback once tracks are available (after user interaction = login click)
  useEffect(() => {
    if (autostartAttempted || tracks.length === 0) return;
    setAutostartAttempted(true);
    setPlaying(true);
  }, [tracks, autostartAttempted]);

  const src = tracks[current] ? musicFileUrl(tracks[current].id) : null;

  // Sync playing state with audio element
  useEffect(() => {
    if (!audioRef.current) return;
    if (playing && src) {
      audioRef.current.play().catch(() => setPlaying(false));
    } else {
      audioRef.current.pause();
    }
  }, [playing, src]);

  const onEnded = () => {
    if (tracks.length === 0) return;
    setCurrent((c) => (c + 1) % tracks.length);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      await api.post("/music/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Musique ajoutée ♡");
      setFile(null);
      setTitle("");
      setOpenUpload(false);
      load();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "Réessaie" });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/music/${id}`);
      const idx = tracks.findIndex((t) => t.id === id);
      const updated = tracks.filter((t) => t.id !== id);
      setTracks(updated);
      if (idx <= current && current > 0) setCurrent((c) => Math.max(0, c - 1));
      if (updated.length === 0) setPlaying(false);
    } catch {
      toast.error("Suppression échouée");
    }
  };

  const currentTrack = tracks[current];

  return (
    <>
      <audio ref={audioRef} src={src || undefined} onEnded={onEnded} muted={muted} />

      <div
        data-testid="music-player"
        className={`fixed bottom-20 right-4 md:bottom-4 md:right-52 z-50 transition-all duration-500 ${
          expanded ? "w-80" : "w-auto"
        }`}
      >
        <div className="romantic-card rounded-2xl p-3 shadow-2xl shadow-rose-950/60 backdrop-blur-2xl">
          {!expanded ? (
            <button
              data-testid="music-toggle"
              onClick={() => setExpanded(true)}
              className="flex items-center gap-3 px-3 py-2 hover:bg-rose-900/30 rounded-xl transition group"
            >
              <div className={`w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center ${playing ? "animate-pulse" : ""}`}>
                <Music className="w-5 h-5 text-white" />
              </div>
              <div className="text-left max-w-[140px]">
                <p className="text-xs text-rose-200/60 uppercase tracking-wider">Notre playlist</p>
                <p className="text-sm text-rose-50 truncate">
                  {currentTrack ? currentTrack.title : "Ajoute une chanson"}
                </p>
              </div>
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-accent text-xl text-rose-300">Notre playlist</p>
                <button
                  data-testid="music-collapse"
                  onClick={() => setExpanded(false)}
                  className="w-7 h-7 rounded-full hover:bg-rose-900/40 text-rose-200 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="rounded-xl bg-[#0F0407]/80 p-3">
                <p className="text-sm text-rose-50 truncate">
                  {currentTrack ? currentTrack.title : "Aucune chanson"}
                </p>
                <p className="text-xs text-rose-200/50 mt-1">
                  {tracks.length > 0 ? `${current + 1} / ${tracks.length}` : "—"}
                </p>

                <div className="flex items-center justify-center gap-2 mt-3">
                  <button
                    data-testid="music-prev"
                    onClick={() => setCurrent((c) => (c - 1 + tracks.length) % Math.max(1, tracks.length))}
                    disabled={tracks.length === 0}
                    className="w-9 h-9 rounded-full hover:bg-rose-900/40 text-rose-200 flex items-center justify-center disabled:opacity-30"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="music-play-pause"
                    onClick={() => setPlaying((p) => !p)}
                    disabled={tracks.length === 0}
                    className="w-11 h-11 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center disabled:opacity-30 transition shadow-lg shadow-rose-900/40"
                  >
                    {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                  </button>
                  <button
                    data-testid="music-next"
                    onClick={() => setCurrent((c) => (c + 1) % Math.max(1, tracks.length))}
                    disabled={tracks.length === 0}
                    className="w-9 h-9 rounded-full hover:bg-rose-900/40 text-rose-200 flex items-center justify-center disabled:opacity-30"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setMuted((m) => !m)}
                    className="w-9 h-9 rounded-full hover:bg-rose-900/40 text-rose-200 flex items-center justify-center ml-2"
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {tracks.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                  {tracks.map((t, i) => (
                    <div
                      key={t.id}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${
                        i === current ? "bg-rose-900/40 text-rose-50" : "text-rose-200/70 hover:bg-rose-900/20"
                      }`}
                      onClick={() => setCurrent(i)}
                    >
                      <span className="truncate flex-1">{t.title}</span>
                      <button
                        data-testid={`music-delete-${t.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(t.id);
                        }}
                        className="text-rose-200/40 hover:text-rose-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Dialog open={openUpload} onOpenChange={setOpenUpload}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="music-upload-open"
                    variant="outline"
                    className="w-full border-rose-700/50 text-rose-100 hover:bg-rose-900/30 rounded-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter une chanson
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Notre chanson</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        data-testid="music-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Notre chanson"
                        className="bg-[#0F0407] border-rose-900/40"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fichier audio (MP3, WAV...)</Label>
                      <Input
                        data-testid="music-file-input"
                        type="file"
                        accept="audio/*"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="bg-[#0F0407] border-rose-900/40"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      data-testid="music-upload-submit"
                      onClick={upload}
                      disabled={!file || uploading}
                      className="bg-rose-600 hover:bg-rose-500 text-white rounded-full"
                    >
                      {uploading ? "Envoi..." : "Ajouter"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
